import { RWaveLoader } from '@rgrd/component-library'
import { useEffect, useRef, useState } from 'react'
import { Navigate, Outlet } from 'react-router'

import { shouldBypassAuthForE2E } from '~/shell/config'
import { useSessionFlags } from '~/shell/flags'
import { LAUNCH_ERROR_PATH } from '~/shell/pages/launch/paths'

import { isReauthRequiredError } from './reauthRequiredError'
import styles from './RequireAuth.module.css'
import { resolveSessionClaims } from './sessionClaims'
import { useRegardAuth } from './useRegardAuth'

/**
 * Guards authenticated routes.
 *
 * A user with no session is sent to get one by `useRegardAuth().reauth`, which for a standalone user is the
 * Auth0 Universal Login and returns them to the URL they asked for. The redirect happens inside a
 * `useEffect` so render stays side-effect free. An EMR-embedded user whose session has expired cannot be
 * given a new one here — only the EHR can start a launch — so they are sent to the launch notice instead.
 * The guard reads the session only through that hook, so it never has to know which of the two modes it is
 * in (Atlas ADR-0027).
 *
 * Protected content is not rendered while loading or redirecting; a centered
 * wave loading indicator (labeled "Logging in…") is shown instead. When
 * authenticated, the matched child route is rendered via `<Outlet />`.
 *
 * Authenticating also resolves the session's claims into `sessionClaimsHolder`,
 * and the guarded tree waits on that too. Doing it here rather than in each
 * consumer lets an accessor like `useEnabledProducts` or `useIdentity` read the claims synchronously
 * during render: the resolve is finished before any guarded route
 * exists, so nothing downstream has a loading state or pops in a beat late
 * (Atlas ADR-0022). An unreadable token yields no claims, while an Auth0 error that says the cached
 * session is dead starts interactive login and keeps this loading boundary mounted.
 *
 * Feature flags resolve behind the same boundary after claims. This keeps `useFlag` synchronous and the
 * wait is bounded so an unavailable flag service cannot block the suite (Atlas ADR-0028).
 *
 * Under the env-gated E2E bypass (`shouldBypassAuthForE2E`, set only by the
 * Playwright smoke harness) the guard renders the outlet directly without ever
 * touching Auth0 — this lets the browser smoke exercise the real shell without a
 * live session. No claims are resolved at all on that path, which is why the
 * bypass's identity and its product grants are each stated outright by the
 * accessor that reads them (`useIdentity`, `getEnabledProductApps`). The bypass is
 * inert in normal dev and production.
 */
export const RequireAuth = () => {
  const {
    getSessionAccessToken,
    hasSession,
    isEmrEmbedded,
    isLoading,
    needsRelaunch,
    reauth,
  } = useRegardAuth()

  const [claimsResolved, setClaimsResolved] = useState(false)
  const flagsResolved = useSessionFlags(claimsResolved)

  // The claims are read once per authenticated mount. Guarding on a ref rather than on the effect's
  // dependencies keeps that true however often `getSessionAccessToken` changes identity, since a
  // fresh function each render would otherwise re-read the token on every render.
  const claimsRequested = useRef(false)

  useEffect(() => {
    if (shouldBypassAuthForE2E || !hasSession || claimsRequested.current) {
      return
    }

    claimsRequested.current = true

    // Deliberately not cancelled on cleanup.
    // The claimsRequested ref makes sure the claims are only resolved once on mount.
    resolveSessionClaims(getSessionAccessToken)
      .then(() => {
        setClaimsResolved(true)
      })
      .catch((error: unknown) => {
        if (!isReauthRequiredError(error)) {
          return
        }

        reauth()
      })
  }, [getSessionAccessToken, hasSession, reauth])

  // Only standalone mode is recovered from here. An EMR-embedded browser is sent to the launch notice by the
  // render below instead, which keeps that navigation inside whichever router is mounted.
  useEffect(() => {
    if (shouldBypassAuthForE2E || isEmrEmbedded || isLoading || hasSession) {
      return
    }

    reauth()
  }, [hasSession, isEmrEmbedded, isLoading, reauth])

  if (shouldBypassAuthForE2E) {
    return <Outlet />
  }

  if (needsRelaunch) {
    return <Navigate replace to={LAUNCH_ERROR_PATH} />
  }

  if (!hasSession || !claimsResolved || !flagsResolved) {
    return (
      <div className={styles.loggingIn}>
        <RWaveLoader label="Logging in…" />
      </div>
    )
  }

  return <Outlet />
}
